import { type ReactNode } from "react";
import { useLpMarkout } from "../hooks/useLpMarkout.ts";

// Same hardcoded palette as the sibling mandate cards.
const TEXT = "#cfe0ff";
const MUTED = "#8a93a6";
const AMBER = "#e0a458";
const GREEN = "#6fcf97";
const BORDER = "rgba(255,255,255,0.08)";
const CARD_BG = "rgba(255,255,255,0.03)";

// Below this many priced fills, the read is low-confidence — surface it.
const LOW_SAMPLE_N = 200;

// Headline band on the ECONOMIC (notional-weighted) markout. Backend-provided.
// Positive markout = LP adversely selected. Neutral institutional labels only.
const BAND: Record<string, { text: string; color: string }> = {
  minimal: { text: "minimal markout", color: GREEN },
  elevated: { text: "elevated markout", color: AMBER },
  high: { text: "adversely selected", color: AMBER },
  favorable: { text: "favorable", color: GREEN },
  unknown: { text: "n/a", color: MUTED },
};

function fmtSigned(n: number | null | undefined, digits = 1): string {
  if (n == null) return "—";
  return `${n > 0 ? "+" : ""}${n.toFixed(digits)}`;
}
function fmtUsd(n: number | null | undefined): string {
  if (n == null) return "—";
  const a = Math.abs(n);
  const s = a >= 1000 ? `$${(a / 1000).toFixed(1)}k` : `$${a.toFixed(0)}`;
  return n < 0 ? `−${s}` : s;
}
// Positive markout = LP picked off (amber); <=0 = not adversely selected (green).
function bpsColor(n: number | null | undefined): string {
  if (n == null) return MUTED;
  return n > 0 ? AMBER : GREEN;
}
function labelHorizon(s: number): string {
  if (s === 0) return "0s";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${s / 60}m`;
  return `${s / 3600}h`;
}

const cardStyle = {
  background: CARD_BG,
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  padding: "12px 14px",
} as const;

const headRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 8,
} as const;

const dividerFoot = {
  fontSize: 11,
  color: MUTED,
  marginTop: 8,
  borderTop: `1px solid ${BORDER}`,
  paddingTop: 7,
} as const;

export function LpMarkoutCard({
  venue,
  pair,
  clientId,
}: {
  venue: string;
  pair: string;
  clientId?: string;
}) {
  const q = useLpMarkout({ venue, pair, clientId });
  const m = q.data;

  const errorFooter = q.isError ? (
    <div style={{ fontSize: 11, color: AMBER, marginTop: 6 }}>could not load</div>
  ) : null;

  let chip: ReactNode;
  let body: ReactNode;

  const headline = m?.horizons?.find(
    (h) => h.horizon_seconds === m?.headline_horizon_seconds,
  );
  const priced = m?.n_ok ?? 0;

  // ---- State: nothing markable -------------------------------------------
  if (!m || priced === 0 || !headline || headline.economic_markout_bps == null) {
    const blockedByEth = !!m?.needs_eth && (m?.n_no_eth_fx ?? 0) > 0 && priced === 0;
    chip = <span style={{ fontSize: 11, color: MUTED }}>n/a</span>;
    body = (
      <>
        <div style={{ fontSize: 24, fontWeight: 500, color: MUTED }}>—</div>
        <div style={{ fontSize: 12, color: MUTED, marginTop: 6, lineHeight: 1.5 }}>
          {blockedByEth
            ? "Awaiting ETH reference for the WETH bridge — no priced markout yet."
            : "No markable swaps against the external reference in the window."}
        </div>
      </>
    );
  } else {
    const band = BAND[m.headline_band ?? "unknown"] ?? BAND.unknown;
    const lowSample = priced < LOW_SAMPLE_N;
    chip = (
      <span
        style={{
          fontSize: 11,
          color: band.color,
          border: `1px solid ${band.color}55`,
          padding: "2px 8px",
          borderRadius: 5,
        }}
      >
        {band.text}
      </span>
    );
    body = (
      <>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span
            style={{
              fontSize: 24,
              fontWeight: 500,
              color: bpsColor(headline.economic_markout_bps),
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {fmtSigned(headline.economic_markout_bps)}
          </span>
          <span style={{ fontSize: 12, color: MUTED }}>
            bps econ @ {labelHorizon(headline.horizon_seconds)}
          </span>
        </div>

        {/* Signature: the economic term structure. Adverse selection is a
            time-revealed quantity, so 0 -> 5 -> 60 -> 300s IS the read. */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${m.horizons.length}, 1fr)`,
            gap: 6,
            marginTop: 12,
          }}
        >
          {m.horizons.map((h) => (
            <div key={h.horizon_seconds} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 10, color: MUTED }}>
                {labelHorizon(h.horizon_seconds)}
              </div>
              <div
                style={{
                  fontSize: 13,
                  marginTop: 2,
                  color: bpsColor(h.economic_markout_bps),
                  fontVariantNumeric: "tabular-nums",
                }}
                title={`${h.n.toLocaleString()} fills · median ${fmtSigned(h.median_markout_bps)} bps · ${fmtUsd(h.markout_usd)}`}
              >
                {fmtSigned(h.economic_markout_bps)}
              </div>
            </div>
          ))}
        </div>

        {/* typical-fill (median) beside cumulative $ — economic is the headline,
            these two contextualize it (heavy-tail tell + dollar impact). */}
        <div style={{ fontSize: 12, color: MUTED, marginTop: 10, lineHeight: 1.5 }}>
          typical fill{" "}
          <span style={{ color: bpsColor(headline.median_markout_bps), fontVariantNumeric: "tabular-nums" }}>
            {fmtSigned(headline.median_markout_bps)}
          </span>{" "}
          bps (median) · cumulative{" "}
          <span style={{ color: TEXT, fontVariantNumeric: "tabular-nums" }}>
            {fmtUsd(headline.markout_usd)}
          </span>
        </div>

        {lowSample ? (
          <div style={{ fontSize: 11, color: AMBER, marginTop: 6 }}>
            limited{m.needs_eth ? " ETH-era" : ""} sample — {priced.toLocaleString()} priced
            {(m.n_no_eth_fx ?? 0) > 0 ? `, ${m.n_no_eth_fx.toLocaleString()} awaiting ETH ref` : ""}
          </div>
        ) : null}

        <div style={{ ...dividerFoot, lineHeight: 1.5 }}>
          <span style={{ color: band.color }}>{band.text}</span>
          <span style={{ color: MUTED, fontVariantNumeric: "tabular-nums" }}>
            {" "}· vs external reference · {priced.toLocaleString()} priced
            {(m.n_stale_reference ?? 0) > 0 ? ` · ${m.n_stale_reference.toLocaleString()} stale` : ""}
          </span>
          {m.single_source_reference ? (
            <div style={{ color: AMBER, marginTop: 3 }}>single-source reference</div>
          ) : null}
        </div>
      </>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={headRow}>
        <span style={{ fontSize: 12, color: MUTED }}>LP markout vs reference</span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: MUTED }}>{m?.window_days ?? 30}d</span>
          {chip}
        </span>
      </div>
      {body}
      {errorFooter}
    </div>
  );
}
